import React, { memo, useMemo } from "react";
import {
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  TrendingUp as PrincipalIcon,
  Percent as InterestIcon,
} from "@mui/icons-material";
import { getNumberFormat } from "../../../utils";
import { DropdownAction } from "../../Dropdown/types";
import ActionsDropdown from "../../Dropdown/ActionsDropdown";
import { PaymentCardProps } from "./types";
import { AmountRow, DateChip, IconWrapper } from "./styles";
import { StyledCard } from "../styles";

const getStatusChipStyles = (status: string) => {
  switch (status) {
    case "pending":
      return {
        backgroundColor: "var(--color-payment-status-pending-background)",
        color: "var(--color-payment-status-pending-color)",
      };
    case "failed":
      return {
        backgroundColor: "var(--color-payment-status-failed-background)",
        color: "var(--color-payment-status-failed-color)",
      };
    case "completed":
    default:
      return {
        backgroundColor: "var(--color-payment-status-complete-background)",
        color: "var(--color-payment-status-complete-color)",
      };
  }
};

export const PaymentCard: React.FC<PaymentCardProps> = memo(
  ({
    paymentDate,
    paidAmount,
    principalAmount,
    interestAmount,
    handleOnDelete,
    handleOnDuplicate,
    handleOnEdit,
    status = "completed",
    paymentId,
    currency = "€",
    compact = false,
    testId,
  }) => {
    const formattedAmounts = useMemo(
      () => ({
        paid: `${getNumberFormat(paidAmount)}${currency}`,
        principal: `${getNumberFormat(principalAmount)}${currency}`,
        interest: `${getNumberFormat(interestAmount)}${currency}`,
      }),
      [paidAmount, principalAmount, interestAmount, currency],
    );

    const percentages = useMemo(
      () => ({
        principal:
          paidAmount > 0
            ? ((principalAmount / paidAmount) * 100).toFixed(1)
            : "0",
        interest:
          paidAmount > 0
            ? ((interestAmount / paidAmount) * 100).toFixed(1)
            : "0",
      }),
      [paidAmount, principalAmount, interestAmount],
    );

    const statusLabel = useMemo(
      () => status.charAt(0).toUpperCase() + status.slice(1),
      [status],
    );

    const dropdownActions: DropdownAction[] = useMemo(() => {
      return [
        {
          id: "edit",
          label: "Edit",
          icon: "✏️",
          onClick: () => {
            handleOnEdit();
          },
          show: true,
        },
        {
          id: "duplicate",
          label: "Duplicate",
          icon: "📋",
          onClick: () => {
            handleOnDuplicate();
          },
          show: true,
        },
        {
          id: "delete",
          label: "Delete",
          icon: "🗑️",
          onClick: () => handleOnDelete(),
          variant: "destructive" as const,
          show: true,
        },
      ];
    }, [handleOnDelete, handleOnDuplicate, handleOnEdit]);

    return (
      <StyledCard
        tabIndex={0}
        aria-label={`Payment ${paymentId} details`}
        elevation={2}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <DateChip
              icon={<CalendarIcon fontSize="small" />}
              label={paymentDate}
              size={compact ? "small" : "medium"}
            />
            <Stack direction="row" spacing={0.2} sx={{ alignItems: "center" }}>
              <Chip
                label={statusLabel}
                size="medium"
                sx={getStatusChipStyles(status)}
              />
              <ActionsDropdown
                compact={true}
                size="sm"
                dropdownActions={dropdownActions}
                testId={testId}
              />
            </Stack>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              color="var(--color-text-secondary)"
              gutterBottom
              sx={{ fontWeight: 500 }}
            >
              Total Payment
            </Typography>
            <Typography
              variant={compact ? "h6" : "h5"}
              component="div"
              sx={{
                fontWeight: 700,
                color: "var(--color-text-default)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {formattedAmounts.paid}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.5}>
            <AmountRow>
              <IconWrapper>
                <PrincipalIcon fontSize="small" />
                <Typography variant="body2" color="var(--color-text-secondary)">
                  Principal ({percentages.principal}%)
                </Typography>
              </IconWrapper>
              <Typography
                variant="body1"
                color="var(--color-text-default)"
                sx={{ fontWeight: 600 }}
              >
                {formattedAmounts.principal}
              </Typography>
            </AmountRow>

            <AmountRow>
              <IconWrapper>
                <InterestIcon fontSize="small" />
                <Typography variant="body2" color="var(--color-text-secondary)">
                  Interest ({percentages.interest}%)
                </Typography>
              </IconWrapper>
              <Typography
                variant="body1"
                color="var(--color-text-default)"
                sx={{ fontWeight: 600 }}
              >
                {formattedAmounts.interest}
              </Typography>
            </AmountRow>
          </Stack>
        </CardContent>
      </StyledCard>
    );
  },
);
